import { FC, Ref, useRef } from "react";
import { useBoolean } from "usehooks-ts";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { TERM_ID_QUERY_PARAM } from "components/constants";
import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { IconText } from "mds/components/IconText";
import { MenuDivider, MenuInstance, MenuItem } from "mds/components/Menu";
import { EyeNotVisibleIcon, EyeVisibleIcon, PencilEditIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectSortedCoursesWithSectionsByTerm } from "store/selectors/section";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsActions } from "utils/routing";

interface TermOptionsMenuProps extends SidebarOptionsMenuProps {
  term: TermType;
  onRename: () => void;
}

export const TermOptionsMenu: FC<TermOptionsMenuProps> = ({
  term,
  onRename,
  className,
  eventPrefix,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const coursesWithSections = useAppSelector((s) =>
    selectSortedCoursesWithSectionsByTerm(s, term?.id),
  );
  const hasSections = coursesWithSections.some(
    (courseWithSections) => courseWithSections.sections.length > 0,
  );
  const isArchived = Boolean(term.archived_at);
  const { deleteQueryParams } = useQueryParamsActions();

  const { value: dialogIsOpen, setTrue: openDialog, setFalse: closeDialog } = useBoolean(false);

  const handleArchive = async () => {
    await storeApi.terms.partial_update({
      id: term.id,
      archived_at: new Date().toISOString(),
    });

    toastSuccessMessage(t("home.toasts.term_archived"));
    menuRef.current?.closeMenu();
  };

  const handleUnarchive = async () => {
    await storeApi.terms.partial_update({
      id: term.id,
      archived_at: null,
    });

    toastSuccessMessage(t("home.toasts.term_unarchived"));
    menuRef.current?.closeMenu();
  };

  const handleDelete = async () => {
    await storeApi.terms.destroy(term.id);

    toastSuccessMessage(t("home.toasts.term_deleted"));

    deleteQueryParams([TERM_ID_QUERY_PARAM]);
  };

  return (
    <>
      <SidebarOptionsMenu
        className={className}
        eventPrefix={eventPrefix}
        instanceRef={menuRef}
        kind="secondary"
        optionsMenuActions={{
          onDelete: openDialog,
          isDeleteDisabled: hasSections,
          deleteTooltipText: hasSections
            ? t("sidebar_options_menu.cannot_delete_term_with_sections")
            : undefined,
        }}
      >
        <MenuItem onClick={onRename}>
          <IconText iconStart={<PencilEditIcon />} text={t("sidebar_options_menu.rename_term")} />
        </MenuItem>

        <MenuDivider />

        {isArchived ? (
          <MenuItem onClick={handleUnarchive}>
            <IconText
              iconStart={<EyeVisibleIcon />}
              text={t("sidebar_options_menu.unarchive_term")}
            />
          </MenuItem>
        ) : (
          <MenuItem onClick={handleArchive}>
            <IconText
              iconStart={<EyeNotVisibleIcon />}
              text={t("sidebar_options_menu.archive_term")}
            />
          </MenuItem>
        )}
      </SidebarOptionsMenu>
      <ConfirmDeletionDialog
        glossaryKey="term"
        instanceName={term.title}
        open={dialogIsOpen}
        onClose={closeDialog}
        onDelete={handleDelete}
      />
    </>
  );
};
